import {
	type ApiErrorResponseErrorItem,
	type ExitConfirmationModalProps,
	type OnStepperFirstButtonClicked,
	type StepperRef,
	useAppLoader,
	useAppType,
	useCountries,
	useIntl,
	usePrevious,
	type UserMobileVerificationRequirementConfig,
	type UserRequirementStateStatus
} from '@capsicum/common';
import {
	type GeetestChallengeMetadata,
	type GeetestSuccessResponse,
	type Requirement,
	useMobileNumberValidation,
	useSmsLoginForm,
	useUser
} from '@capsicum/socios-core';
import { type RefObject, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { useSendCodeVerificationBySMS, useSetMobileVerificationRequirementStateQuery } from '../data-access';

type MobileVerificationProps = {
	onCodeVerificationSuccess: () => void;
	onDismiss: () => void;
	openExitConfirmationModal: (props: ExitConfirmationModalProps) => void;
	status?: UserRequirementStateStatus;
	stepperRef: RefObject<StepperRef>;
	triggerCaptcha: (() => Promise<GeetestSuccessResponse>) | (() => void);
};

export const useMobileVerification = (
	requirement: Requirement<UserMobileVerificationRequirementConfig>,
	{
		onCodeVerificationSuccess,
		onDismiss,
		openExitConfirmationModal,
		status,
		stepperRef,
		triggerCaptcha
	}: MobileVerificationProps
) => {
	const { $t, locale } = useIntl();
	const appLoader = useAppLoader();
	const checkMobileNumberAvailability = useMobileNumberValidation();
	const mobileNumberForm = useSmsLoginForm({ preventPrefillData: true });
	const appType = useAppType();
	const { countries, getCountryBy, isLoading: isCountriesLoading } = useCountries();
	const { refetchUser } = useUser();
	const [captchaInfo, setCaptchaInfo] = useState(null as GeetestSuccessResponse | null);
	const [mobileNumberWithPrefix, setMobileNumberWithPrefix] = useState('');
	const [currentStep, setCurrentStep] = useState(0);
	const [codeVerificationError, setCodeVerificationError] = useState<ApiErrorResponseErrorItem | null>(null);
	const previousStep = usePrevious(currentStep);

	const {
		error: submitMobileVerificationCodeError,
		isSuccess: isCodeVerificationSuccess,
		mutateAsync: submitVerificationCode
	} = useSetMobileVerificationRequirementStateQuery(refetchUser);
	const {
		error: errorSendingCode,
		isLoading: isSendCodeBySMSLoading,
		mutateAsync: sendCodeVerificationBySMS
	} = useSendCodeVerificationBySMS(stepperRef.current?.goToNextStep);

	const isReadonlyMode = status === 'completed_allow';
	const mobileNumber = useWatch({ control: mobileNumberForm.control, name: 'mobileNumber' });
	const countryCode = useWatch({ control: mobileNumberForm.control, name: 'country.iso2' });

	const resetFormWithValues = (initialCountryCode: string, initialMobileNumber: string) => {
		const country = initialCountryCode ? getCountryBy(initialCountryCode, 'iso2') : null;

		if (country) {
			mobileNumberForm.reset({
				country,
				mobileNumber: initialMobileNumber
			});
		}
	};

	useEffect(() => {
		if (!countries?.length) return;

		const prefilledCountry = requirement.config.prefill.mobileNumber?.countryCode;
		const prefilledMobileNumber = requirement.config.prefill.mobileNumber?.number;

		if (prefilledCountry && prefilledMobileNumber) resetFormWithValues(prefilledCountry, prefilledMobileNumber);
	}, [countries]);

	const sendCodeBySMS = () => {
		if (captchaInfo?.captcha_id) {
			sendCodeVerificationBySMS({
				captchaInformation: {
					appContext: 'capsicum',
					appType,
					captchaOutput: captchaInfo.captcha_output,
					genTime: captchaInfo.gen_time,
					lotNumber: captchaInfo.lot_number,
					passToken: captchaInfo.pass_token,
					version: 'v4'
				},
				countryCode,
				mobileNumber
			});
		}
	};

	const captchaChallengeMetadata = useMemo<GeetestChallengeMetadata>(() => {
		return {
			details: {
				countryCode,
				mobileNumber
			},
			type: 'phone-number'
		};
	}, [mobileNumber, countryCode]);

	useEffect(() => {
		const errorMessage = errorSendingCode?.response?.data?.errors?.[0];

		if (errorMessage) {
			const message = (errorMessage?.translations as Record<string, string>)?.[locale] || errorMessage?.title;

			mobileNumberForm.setError('mobileNumber', { message });
		}
	}, [errorSendingCode]);

	useEffect(() => {
		if (isCodeVerificationSuccess) onCodeVerificationSuccess();
	}, [isCodeVerificationSuccess]);

	useEffect(() => {
		const verificationError = submitMobileVerificationCodeError?.response?.data?.errors?.[0];

		if (verificationError) {
			setCodeVerificationError(verificationError);
		}
	}, [submitMobileVerificationCodeError]);

	useEffect(() => {
		if (currentStep === 0 && previousStep === 1) {
			setCaptchaInfo(null);
			setCodeVerificationError(null);
		}
	}, [currentStep]);

	useEffect(sendCodeBySMS, [captchaInfo]);

	const submitMobileNumberForm = mobileNumberForm.handleSubmit(async data => {
		const { error, formattedValues } = await checkMobileNumberAvailability({
			countryCode: data.country.iso2,
			mobileNumber: data.mobileNumber
		});

		if (error) {
			const { field, ...errorDetails } = error;

			mobileNumberForm.setError(field, errorDetails);
		} else if (formattedValues) {
			if (formattedValues.canRegister === false) {
				mobileNumberForm.setError('country', {
					message: $t('jurisdictionRequirements.mobileVerification.blockedCountryError')
				});
			} else if (formattedValues.isAvailable) {
				setMobileNumberWithPrefix(formattedValues.mobileNumber);
				triggerCaptcha();
				mobileNumberForm.clearErrors();
			} else {
				mobileNumberForm.setError('mobileNumber', {
					message: $t('jurisdictionRequirements.mobileVerification.mobileNumberNotAvailable')
				});
			}
		} else {
			mobileNumberForm.setError('mobileNumber', {
				message: $t('error.defaultDescription')
			});
		}
	});

	const onNextStep: OnStepperFirstButtonClicked = (_, currentStep) => {
		if (isReadonlyMode) onDismiss();
		else if (currentStep === 0) submitMobileNumberForm();
	};

	const onSecondaryButtonClick = () => {
		if (currentStep === 1) openExitConfirmationModal({ onConfirmation: onDismiss, testID: 'mobile-verification' });
		else onDismiss();
	};

	const onChangeTargetClicked = () => resetFormWithValues(countryCode, '');

	const onCodeSubmit = (code: string) =>
		appLoader(
			submitVerificationCode({
				countryCode,
				generatedCode: code,
				mobileNumber
			})
		);

	const isFirstButtonLoading = mobileNumberForm.formState.isSubmitting || isSendCodeBySMSLoading;
	const isUserOnCodeInputStep = currentStep === 1;

	const mobileCodeVerificationError =
		(codeVerificationError?.translations as Record<string, string>)?.[locale] || codeVerificationError?.title;

	return {
		captchaChallengeMetadata,
		isFirstButtonLoading,
		isFormLoading: isCountriesLoading,
		isReadonlyMode,
		isUserOnCodeInputStep,
		mobileCodeVerificationError,
		mobileNumberForm,
		mobileNumberWithPrefix,
		onChangeTargetClicked,
		onCodeSubmit,
		onNextStep,
		onSecondaryButtonClick,
		setCaptchaInfo,
		setCurrentStep
	};
};
